export function filterData(data, filters) {
  // Optimized: pre-compile active filters to avoid Object.entries overhead
  const activeFilters = [];
  for (const key in filters) {
    if (filters[key]) {
      activeFilters.push([key, filters[key]]);
    }
  }

  // Fast path: no filters active
  if (activeFilters.length === 0) return data;

  return data.filter(item => {
    // Direct loop instead of .every() for better performance
    for (let i = 0; i < activeFilters.length; i++) {
      const [key, value] = activeFilters[i];

      if (key === "rangoEdad") {
        const dashIndex = value.indexOf('-');
        const min = parseInt(value.substring(0, dashIndex));
        const max = parseInt(value.substring(dashIndex + 1));
        if (item.edad < min || item.edad > max) return false;
        continue;
      }

      if (key === "diaJornada") {
        let filterValue = value;

        const lastDashIndex = value.lastIndexOf('-');
        if (lastDashIndex > 0) {
          const possibleYear = value.substring(lastDashIndex + 1);
          if (/^\d{4}$/.test(possibleYear)) {
            filterValue = value.substring(0, lastDashIndex);
          }
        }
        if (filterValue.includes('-')) {
          const dashIndex = filterValue.indexOf('-');
          const dia = filterValue.substring(0, dashIndex);
          const jornada = filterValue.substring(dashIndex + 1);
          if (item.dia !== dia || item.jornada !== jornada) return false;
        } else if (item.dia !== filterValue && item.jornada !== filterValue) {
          return false;
        }
        continue;
      }

      if (item[key] !== value) return false;
    }
    return true;
  });
}


const ordenMeses = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"
];

// Año actual y anterior dinámicos
export const CURRENT_YEAR = new Date().getFullYear();
export const COMPARISON_YEAR = CURRENT_YEAR - 1;


export function getTotals(filteredData, comparisonYear = COMPARISON_YEAR, currentYear = CURRENT_YEAR) {
  let total1 = 0;
  let total2 = 0;
  filteredData.forEach(item => {
    if (item.año === comparisonYear) total1++;
    else if (item.año === currentYear) total2++;
  });
  const diferencia = total2 - total1;
  const variacion = total1 ? ((diferencia / total1) * 100).toFixed(1) : 0;
  return { total1, total2, diferencia, variacion };
}

export function groupByField(filteredData, field, comparisonYear = COMPARISON_YEAR, currentYear = CURRENT_YEAR) {
  // Optimized: use Map to count in a single pass instead of filtering multiple times
  const countsMap = new Map();

  // Single pass: build counts for both years for each value
  filteredData.forEach(item => {
    const value = item[field] || "Sin dato";
    const year = item.año;

    if (!countsMap.has(value)) {
      countsMap.set(value, { [comparisonYear]: 0, [currentYear]: 0 });
    }

    const counts = countsMap.get(value);
    if (year === comparisonYear) {
      counts[comparisonYear]++;
    } else if (year === currentYear) {
      counts[currentYear]++;
    }
  });

  // Convert map to array
  let valores = Array.from(countsMap.keys());

  // Sort months if needed
  if (field === "mes") {
    valores.sort((a, b) => {
      const indexA = ordenMeses.indexOf(a.toLowerCase());
      const indexB = ordenMeses.indexOf(b.toLowerCase());
      return indexA - indexB;
    });
  }

  return valores.map(value => ({
    name: value,
    ...countsMap.get(value)
  }));
}

export function groupByDayShift(filteredData, año, dias, jornadas) {
  // Optimized: accumulate counts in a single pass instead of filtering multiple times
  const countsMap = new Map();

  // Initialize structure
  dias.forEach(dia => {
    const jornadaCounts = {};
    jornadas.forEach(jornada => {
      jornadaCounts[jornada] = 0;
    });
    countsMap.set(dia, jornadaCounts);
  });

  // Single pass: count all combinations
  filteredData.forEach(item => {
    if (item.año === año && countsMap.has(item.dia)) {
      const jornadaCounts = countsMap.get(item.dia);
      if (item.jornada in jornadaCounts) {
        jornadaCounts[item.jornada]++;
      }
    }
  });

  // Convert to expected format
  return dias.map(dia => ({
    dia,
    ...countsMap.get(dia)
  }));
}

export function groupAgeByRange(filteredData, ranges, comparisonYear = COMPARISON_YEAR, currentYear = CURRENT_YEAR) {
  // Ultra-optimized: count all ranges in a single pass for each year
  const rangeCounts = ranges.map(() => ({ [comparisonYear]: 0, [currentYear]: 0 }));

  // Single pass: iterate data once and count for all ranges
  filteredData.forEach(item => {
    const year = item.año;
    if (year !== comparisonYear && year !== currentYear) return;

    // Find which range this item belongs to
    for (let i = 0; i < ranges.length; i++) {
      const rango = ranges[i];
      const isBlank = rango.label === "Sin Dato" || rango.label === "Blank";

      if (isBlank && !item.edad) {
        rangeCounts[i][year]++;
        break; // Item can only belong to one range
      } else if (!isBlank && item.edad >= (rango.min ?? 0) && item.edad <= (rango.max ?? 0)) {
        rangeCounts[i][year]++;
        break; // Item can only belong to one range
      }
    }
  });

  // Build result array
  return ranges.map((rango, i) => ({
    rango: rango.label,
    ...rangeCounts[i]
  })).filter(entry => entry[comparisonYear] > 0 || entry[currentYear] > 0);
}

export function groupByFieldWithCategories(data, groupField, categoryField, categories, categoryMapper) {
  const result = {};

  data.forEach(item => {
    const groupValue = item[groupField];

    if (!result[groupValue]) {
      result[groupValue] = { name: groupValue };
      categories.forEach(cat => result[groupValue][cat] = 0);
    }

    const category = categoryMapper(item[categoryField]);

    if (categories.includes(category)) {
      result[groupValue][category] += 1;
    }
  });

  let values = Object.values(result);

  if (groupField === "mes") {
    values = values.sort((a, b) => {
      const indexA = ordenMeses.indexOf((a.name || "").toLowerCase());
      const indexB = ordenMeses.indexOf((b.name || "").toLowerCase());
      return indexA - indexB;
    });
  }

  return values;
}

export function processFilteredData(filteredData, config, comparisonYear = COMPARISON_YEAR, currentYear = CURRENT_YEAR) {
  const results = {};

  // Initialize grouping maps based on config
  const groupMaps = {};
  if (config.groupByFields) {
    config.groupByFields.forEach(field => {
      groupMaps[field] = new Map();
    });
  }

  // Initialize day/shift map if needed
  let dayShiftMap = null;
  if (config.includeDayShift && config.dias && config.jornadas) {
    dayShiftMap = new Map();
    config.dias.forEach(dia => {
      const jornadaCounts = { [comparisonYear]: {}, [currentYear]: {} };
      config.jornadas.forEach(jornada => {
        jornadaCounts[comparisonYear][jornada] = 0;
        jornadaCounts[currentYear][jornada] = 0;
      });
      dayShiftMap.set(dia, jornadaCounts);
    });
  }

  // Initialize totals
  let total1 = 0;
  let total2 = 0;

  // SINGLE PASS: Process all aggregations at once
  filteredData.forEach(item => {
    const year = item.año;

    // Count totals
    if (config.includeTotals) {
      if (year === comparisonYear) total1++;
      else if (year === currentYear) total2++;
    }

    // Process groupBy fields
    if (config.groupByFields && (year === comparisonYear || year === currentYear)) {
      config.groupByFields.forEach(field => {
        const value = item[field] || "Sin dato";
        const map = groupMaps[field];

        if (!map.has(value)) {
          map.set(value, { [comparisonYear]: 0, [currentYear]: 0 });
        }
        map.get(value)[year]++;
      });
    }

    // Process day/shift
    if (dayShiftMap && item.dia && item.jornada && (year === comparisonYear || year === currentYear)) {
      const dayCounts = dayShiftMap.get(item.dia);
      if (dayCounts && dayCounts[year][item.jornada] !== undefined) {
        dayCounts[year][item.jornada]++;
      }
    }
  });

  // Build results object
  if (config.includeTotals) {
    const diferencia = total2 - total1;
    results.totals = {
      total1,
      total2,
      diferencia,
      variacion: total1 ? ((diferencia / total1) * 100).toFixed(1) : 0
    };
    results.resumenData = [
      { name: comparisonYear, [comparisonYear]: total1, [currentYear]: 0 },
      { name: currentYear, [comparisonYear]: 0, [currentYear]: total2 }
    ];
  }

  // Build groupBy results
  if (config.groupByFields) {
    results.groups = {};
    config.groupByFields.forEach(field => {
      let valores = Array.from(groupMaps[field].keys());

      // Sort months if needed
      if (field === "mes") {
        valores.sort((a, b) => {
          const indexA = ordenMeses.indexOf(a.toLowerCase());
          const indexB = ordenMeses.indexOf(b.toLowerCase());
          return indexA - indexB;
        });
      }

      results.groups[field] = valores.map(value => ({
        name: value,
        ...groupMaps[field].get(value)
      }));
    });
  }

  // Build day/shift results
  if (dayShiftMap) {
    results.diasJornadaData = {};
    results.diasJornadaData[comparisonYear] = config.dias.map(dia => ({
      dia,
      ...dayShiftMap.get(dia)[comparisonYear]
    }));
    results.diasJornadaData[currentYear] = config.dias.map(dia => ({
      dia,
      ...dayShiftMap.get(dia)[currentYear]
    }));
  }

  return results;
}

export const applyFilterLogic = (prevfilters, tipo, value, source, frozenKey, setFrozenKey, filtersBase) => {
  const isSameFilter = prevfilters[tipo] === value;
  const hasAnyFilter = Object.values(prevfilters).some(f => f !== null);

  if (hasAnyFilter && !isSameFilter && frozenKey !== source) {
    setFrozenKey(null);
    return filtersBase;
  }

  setFrozenKey(source);

  return {
    ...filtersBase,
    [tipo]: isSameFilter ? null : value,
  };
};

export const handleClickLogic = ({
  data,
  xKey,
  selectedCategory,
  isFrozen,
  showData,
  setIsFrozen,
  setFrozenData,
  setSelectedCategory,
  onBarClick,
}) => {
  const clickedCategory = data?.payload?.[xKey];
  const isSameSelection = selectedCategory === clickedCategory;
  const newSelection = isSameSelection ? null : clickedCategory;

  if (isFrozen && !isSameSelection) {
    if (onBarClick) onBarClick(newSelection ? { category: clickedCategory } : null);
    return;
  }

  if (isSameSelection && isFrozen) {
    setIsFrozen(false);
    setFrozenData(null);
    if (onBarClick) onBarClick(null);
    return;
  }

  if (!isFrozen) {
    setIsFrozen(true);
    setFrozenData([...showData]);
  }

  setSelectedCategory(newSelection);
  if (onBarClick) onBarClick(newSelection ? { category: clickedCategory } : null);
};